import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Label,
  LabelList,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '~/i18n';
import { CHART_FONT_FAMILY } from '~/styles/chart';

type PHPoint = {
  h: number;
  p: number;
  point?: string;
  label?: string;
};

type PHIsothermSeries = {
  name: string;
  points: PHPoint[];
  labelPoint: PHPoint | null;
};

type PHDiagramData = {
  saturationLiquid: PHPoint[];
  saturationVapor: PHPoint[];
  cycle: PHPoint[];
  isotherms: PHIsothermSeries[];
  xDomain: [number, number];
  yDomain: [number, number];
  yTicks: number[];
};

type Props = {
  data: PHDiagramData | null;
  height?: number;
  isLoading?: boolean;
  getTextFunc?: (key: string) => string;
};

type Domains = {
  xDomain: [number, number];
  yDomain: [number, number];
};

type HoverInfo = {
  h: number;
  p: number;
  cyclePoint: PHPoint | null;
};

type SelectionBox = {
  left: number | null;
  right: number | null;
  top: number | null;
  bottom: number | null;
};

type IsothermLabel = {
  h: number;
  p: number;
  label: string;
  angle: number;
};

const COR_FUNDO = '#f4f6f8';
const COR_GRADE = '#d8dee4';
const COR_SAT = '#1565c0';
const COR_ISO = '#7fb3ff';
const COR_ISO_LABEL = '#4b6f9c';
const COR_CICLO = '#c62828';
const COR_CURSOR = '#6f6f6f';
const COR_TEXTO = '#37474f';

const isFiniteNumber = (value: number): boolean => Number.isFinite(value);
const isPositiveNumber = (value: number): boolean => Number.isFinite(value) && value > 0;

function buildLogTicks(domain: [number, number]): number[] {
  const min = domain[0];
  const max = domain[1];

  if (!isPositiveNumber(min) || !isPositiveNumber(max) || max <= min) {
    return [];
  }

  const ticks: number[] = [];
  const start = Math.floor(Math.log10(min));
  const end = Math.ceil(Math.log10(max));
  const multipliers = [1, 2, 4, 6, 8];

  for (let exp = start; exp <= end; exp += 1) {
    for (let i = 0; i < multipliers.length; i += 1) {
      const value = multipliers[i] * 10 ** exp;
      if (value >= min * 0.999 && value <= max * 1.001) {
        ticks.push(value);
      }
    }
  }

  if (!ticks.length) {
    return [min, max];
  }

  return Array.from(
    new Set(ticks.map((value) => Math.round(value * 1000) / 1000))
  ).sort((a, b) => a - b);
}

function normalizeDistance(h: number, p: number, point: PHPoint, domains: Domains): number {
  const xSpan = Math.max(1, domains.xDomain[1] - domains.xDomain[0]);
  const yMin = Math.log10(Math.max(domains.yDomain[0], 0.001));
  const yMax = Math.log10(Math.max(domains.yDomain[1], 0.002));
  const ySpan = Math.max(0.01, yMax - yMin);
  const dx = (point.h - h) / xSpan;
  const dy = (Math.log10(point.p) - Math.log10(p)) / ySpan;
  return Math.sqrt(dx * dx + dy * dy);
}

function findNearestCyclePoint(h: number, p: number, cycle: PHPoint[], domains: Domains): PHPoint | null {
  let bestPoint: PHPoint | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let i = 0; i < cycle.length; i += 1) {
    const point = cycle[i];
    const distance = normalizeDistance(h, p, point, domains);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestPoint = point;
    }
  }

  return bestDistance <= 0.09 ? bestPoint : null;
}

function extractHoverInfo(state: any, domains: Domains, cycle: PHPoint[]): HoverInfo | null {
  if (!state) {
    return null;
  }

  let h: number | null = null;
  let p: number | null = null;

  if (isFiniteNumber(Number(state.activeLabel))) {
    h = Number(state.activeLabel);
  }

  if (state.activePayload && state.activePayload.length) {
    for (let i = 0; i < state.activePayload.length; i += 1) {
      const item = state.activePayload[i];
      if (h === null && item && item.payload && isFiniteNumber(Number(item.payload.h))) {
        h = Number(item.payload.h);
      }
      if (p === null && item && item.payload && isPositiveNumber(Number(item.payload.p))) {
        p = Number(item.payload.p);
      }
      if (p === null && item && isPositiveNumber(Number(item.value))) {
        p = Number(item.value);
      }
    }
  }

  if (h === null || p === null || !isFiniteNumber(h) || !isPositiveNumber(p)) {
    return null;
  }

  return {
    h,
    p,
    cyclePoint: findNearestCyclePoint(h, p, cycle, domains),
  };
}

function formatNumber(value: number, digits: number): string {
  if (!isFiniteNumber(value)) {
    return '-';
  }
  return value.toFixed(digits);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function computeAngle(points: PHPoint[], index: number): number {
  const prev = points[Math.max(0, index - 2)];
  const next = points[Math.min(points.length - 1, index + 2)];

  if (
    !prev ||
    !next ||
    !isFiniteNumber(prev.h) ||
    !isFiniteNumber(next.h) ||
    !isPositiveNumber(prev.p) ||
    !isPositiveNumber(next.p)
  ) {
    return -18;
  }

  const dx = next.h - prev.h;
  const dy = Math.log10(next.p) - Math.log10(prev.p);

  if (!isFiniteNumber(dx) || !isFiniteNumber(dy) || Math.abs(dx) < 1e-6) {
    return -18;
  }

  const angle = ((Math.atan2(dy, dx) * 180) / Math.PI) * 0.65;
  return clamp(angle, -38, 24);
}

function buildIsothermLabels(data: PHDiagramData, domains: Domains): IsothermLabel[] {
  if (!data.isotherms || !data.isotherms.length) {
    return [];
  }

  const labels: IsothermLabel[] = [];
  const spanX = Math.max(1, domains.xDomain[1] - domains.xDomain[0]);
  const minX = domains.xDomain[0] + spanX * 0.08;
  const maxX = domains.xDomain[1] - spanX * 0.08;

  data.isotherms.forEach((iso) => {
    if (iso && iso.points && iso.points.length >= 8) {
      const baseIndex = Math.max(
        3,
        Math.min(iso.points.length - 4, Math.floor(iso.points.length * 0.72))
      );

      const point = iso.points[baseIndex];

      if (
        point &&
        point.label &&
        isFiniteNumber(point.h) &&
        isPositiveNumber(point.p)
      ) {
        const safeH = clamp(point.h, minX, maxX);
        const safeP = clamp(
          point.p,
          domains.yDomain[0] * 1.12,
          domains.yDomain[1] * 0.9
        );

        const angle = computeAngle(iso.points, baseIndex);

        labels.push({
          h: safeH,
          p: safeP,
          label: point.label,
          angle,
        });
      }
    }
  });

  labels.sort((a, b) => a.h - b.h);

  const filtered: IsothermLabel[] = [];

  labels.forEach((current) => {
    const previous = filtered.length ? filtered[filtered.length - 1] : null;

    if (!previous || Math.abs(current.h - previous.h) >= spanX * 0.06) {
      filtered.push(current);
    }
  });

  return filtered;
}

function buildTopTemperatureLabels(data: PHDiagramData, domains: Domains): PHPoint[] {
  if (!data.isotherms || !data.isotherms.length) {
    return [];
  }

  const labels: PHPoint[] = [];
  const spanX = Math.max(1, domains.xDomain[1] - domains.xDomain[0]);
  const minGap = Math.max(18, spanX * 0.06);
  const minX = domains.xDomain[0] + spanX * 0.04;
  const maxX = domains.xDomain[1] - spanX * 0.04;

  data.isotherms.forEach((iso) => {
    const labelPoint = iso && iso.labelPoint ? iso.labelPoint : null;

    if (
      labelPoint &&
      labelPoint.label &&
      isFiniteNumber(labelPoint.h)
    ) {
      labels.push({
        h: clamp(labelPoint.h, minX, maxX),
        p: domains.yDomain[1] * 0.985,
        label: labelPoint.label,
      });
    }
  });

  labels.sort((a, b) => a.h - b.h);

  const filtered: PHPoint[] = [];
  labels.forEach((current) => {
    const previous = filtered.length ? filtered[filtered.length - 1] : null;
    if (!previous || Math.abs(current.h - previous.h) >= minGap) {
      filtered.push(current);
    }
  });

  return filtered.map((item, index) => ({
    h: item.h,
    p: domains.yDomain[1] * (index % 2 === 0 ? 0.985 : 0.94),
    label: item.label,
  }));
}

function CustomCrosshairCursor(props: any) {
  const points = props && props.points ? props.points : null;
  const point = points && points.length ? points[0] : null;

  if (!point) {
    return null;
  }

  const left = Number(props.left || 0);
  const top = Number(props.top || 0);
  const width = Number(props.width || 0);
  const height = Number(props.height || 0);

  return (
    <g>
      <line
        x1={point.x}
        x2={point.x}
        y1={top}
        y2={top + height}
        stroke={COR_CURSOR}
        strokeDasharray="4 4"
        strokeWidth={1}
      />
      <line
        x1={left}
        x2={left + width}
        y1={point.y}
        y2={point.y}
        stroke={COR_CURSOR}
        strokeDasharray="4 4"
        strokeWidth={1}
      />
    </g>
  );
}

function CustomTooltip(props: any) {
  const active = props && props.active;
  const hoverInfo = props && props.hoverInfo ? props.hoverInfo : null;
  const payload = props && props.payload ? props.payload : [];

  if (!active || (!hoverInfo && (!payload || !payload.length))) {
    return null;
  }

  const point = hoverInfo && hoverInfo.cyclePoint ? hoverInfo.cyclePoint : null;

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.98)',
        border: '1px solid #c8d1dc',
        borderRadius: 4,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        padding: 10,
        fontSize: 14,
        minWidth: 160,
        color: COR_TEXTO,
      }}
    >
      <div><b>h:</b> {formatNumber(hoverInfo ? hoverInfo.h : Number(payload[0] && payload[0].payload ? payload[0].payload.h : NaN), 2)} kJ/kg</div>
      <div><b>p:</b> {formatNumber(hoverInfo ? hoverInfo.p : Number(payload[0] && payload[0].payload ? payload[0].payload.p : NaN), 3)} bar</div>
      {point ? (
        <>
          <div><b>Ponto do ciclo:</b> {point.point || '-'}</div>
          <div><b>h ponto:</b> {formatNumber(point.h, 2)} kJ/kg</div>
          <div><b>p ponto:</b> {formatNumber(point.p, 3)} bar</div>
        </>
      ) : null}
    </div>
  );
}

function buildCycleZoom(cycle: PHPoint[]): Domains {
  const uniqueCycle = cycle.filter((point, index) => !(index === cycle.length - 1 && point.point === '1'));
  const enthalpies = uniqueCycle.map((point) => point.h);
  const pressures = uniqueCycle.map((point) => point.p);
  const minH = Math.min.apply(null, enthalpies);
  const maxH = Math.max.apply(null, enthalpies);
  const minP = Math.min.apply(null, pressures);
  const maxP = Math.max.apply(null, pressures);

  return {
    xDomain: [Math.floor((minH - 20) / 10) * 10, Math.ceil((maxH + 20) / 10) * 10],
    yDomain: [Math.max(0.5, minP * 0.75), maxP * 1.35],
  };
}

function renderIsothermLabel(props: any) {
  if (!props || !props.x || !props.y || !props.value) {
    return null;
  }

  const x = Number(props.x);
  const y = Number(props.y);
  const value = String(props.value);
  const angle =
    props &&
    props.payload &&
    isFiniteNumber(Number(props.payload.angle))
      ? Number(props.payload.angle)
      : -18;

  return (
    <g transform={`translate(${x},${y}) rotate(${angle})`}>
      <rect
        x={-16}
        y={-10}
        width={32}
        height={14}
        rx={2}
        ry={2}
        fill="rgba(244,246,248,0.88)"
      />
      <text
        x={0}
        y={0}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={13}
        fontWeight={500}
        fill={COR_ISO_LABEL}
      >
        {value}
      </text>
    </g>
  );
}

export function PHDiagramChart({
  data,
  height = 460,
  isLoading = false,
  getTextFunc = (key) => key,
}: Props) {
  const { t } = useI18n();
  const [domains, setDomains] = useState<Domains | null>(null);
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const [selection, setSelection] = useState<SelectionBox>({
    left: null,
    right: null,
    top: null,
    bottom: null,
  });

  useEffect(() => {
    if (data) {
      setDomains({ xDomain: data.xDomain, yDomain: data.yDomain });
      setSelection({ left: null, right: null, top: null, bottom: null });
    }
  }, [data]);

  const currentDomains = domains || (data ? { xDomain: data.xDomain, yDomain: data.yDomain } : null);

  const isothermLabels = useMemo(() => {
    if (!data || !currentDomains) {
      return [];
    }
    return buildIsothermLabels(data, currentDomains);
  }, [data, currentDomains]);

  const topTemperatureLabels = useMemo(() => {
    if (!data || !currentDomains) {
      return [];
    }
    return buildTopTemperatureLabels(data, currentDomains);
  }, [data, currentDomains]);

  const yTicks = useMemo(() => {
    if (!currentDomains) {
      return [];
    }
    return buildLogTicks(currentDomains.yDomain);
  }, [currentDomains]);

  if (isLoading) {
    return <div style={{ width: '100%', height }}>{t('Gerando diagrama...')}</div>;
  }

  if (!data || !currentDomains) {
    return <div style={{ width: '100%', height }}>{t('Sem dados')}</div>;
  }

  const handleResetZoom = () => {
    setDomains({ xDomain: data.xDomain, yDomain: data.yDomain });
    setSelection({ left: null, right: null, top: null, bottom: null });
  };

  const handleCycleZoom = () => {
    setDomains(buildCycleZoom(data.cycle));
    setSelection({ left: null, right: null, top: null, bottom: null });
  };

  const handleMouseDown = (state: any) => {
    const info = extractHoverInfo(state, currentDomains, data.cycle);
    if (!info) {
      return;
    }

    setSelection({
      left: info.h,
      right: info.h,
      top: info.p,
      bottom: info.p,
    });
  };

  const handleMouseMove = (state: any) => {
    const info = extractHoverInfo(state, currentDomains, data.cycle);
    setHoverInfo(info);

    if (!info || selection.left === null) {
      return;
    }

    setSelection({
      left: selection.left,
      right: info.h,
      top: selection.top,
      bottom: info.p,
    });
  };

  const handleMouseUp = () => {
    if (
      selection.left === null ||
      selection.right === null ||
      selection.top === null ||
      selection.bottom === null
    ) {
      setSelection({ left: null, right: null, top: null, bottom: null });
      return;
    }

    const nextX0 = Math.min(selection.left, selection.right);
    const nextX1 = Math.max(selection.left, selection.right);
    const nextY0 = Math.min(selection.top, selection.bottom);
    const nextY1 = Math.max(selection.top, selection.bottom);

    if (Math.abs(nextX1 - nextX0) < 8 || Math.abs(nextY1 - nextY0) < 0.2) {
      setSelection({ left: null, right: null, top: null, bottom: null });
      return;
    }

    setDomains({
      xDomain: [nextX0, nextX1],
      yDomain: [Math.max(0.5, nextY0), nextY1],
    });
    setSelection({ left: null, right: null, top: null, bottom: null });
  };

  return (
    <div
      style={{
        width: '100%',
        height,
        fontFamily: CHART_FONT_FAMILY,
        background: COR_FUNDO,
        padding: 10,
        border: '1px solid #d5dde5',
        borderRadius: 6,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5)',
      }}
    >
      {/* <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 6 }}>
        <button
          type="button"
          onClick={handleCycleZoom}
          style={{
            padding: '5px 12px',
            cursor: 'pointer',
            border: '1px solid #b8c4d0',
            borderRadius: 4,
            background: '#ffffff',
            color: COR_TEXTO,
          }}
        >
          {t('Zoom ciclo')}
        </button>
        <button
          type="button"
          onClick={handleResetZoom}
          style={{
            padding: '5px 12px',
            cursor: 'pointer',
            border: '1px solid #b8c4d0',
            borderRadius: 4,
            background: '#ffffff',
            color: COR_TEXTO,
          }}
        >
          {t('Reset zoom')}
        </button>
      </div> */}

      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          margin={{ top: 46, right: 18, left: 6, bottom: 58 }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => setHoverInfo(null)}
          onDoubleClick={handleResetZoom}
        >
          <CartesianGrid stroke={COR_GRADE} strokeDasharray="3 3" />

          <XAxis
            type="number"
            dataKey="h"
            domain={currentDomains.xDomain}
            allowDataOverflow
            tick={{ fontSize: 14, fill: COR_TEXTO }}
            tickFormatter={(value) => `${Math.round(Number(value))}`}
            axisLine={{ stroke: '#8fa1b3' }}
            tickLine={{ stroke: '#8fa1b3' }}
          >
            <Label
              value={`${getTextFunc('enthalpy')} [kJ/kg]`}
              position="insideBottom"
              offset={-10}
              style={{ fill: COR_TEXTO, fontSize: 15, fontWeight: 600 }}
            />
          </XAxis>

          <YAxis
            type="number"
            dataKey="p"
            scale="log"
            domain={currentDomains.yDomain}
            ticks={yTicks}
            allowDataOverflow
            width={54}
            tick={{ fontSize: 14, fill: COR_TEXTO }}
            tickFormatter={(value) => `${Number(value)}`}
            axisLine={{ stroke: '#8fa1b3' }}
            tickLine={{ stroke: '#8fa1b3' }}
          >
            <Label
              value={`${getTextFunc('pressure')} [bar]`}
              angle={-90}
              position="insideLeft"
              style={{ textAnchor: 'middle', fill: COR_TEXTO, fontSize: 15, fontWeight: 600 }}
            />
          </YAxis>

          <Tooltip
            content={(props) => <CustomTooltip {...props} hoverInfo={hoverInfo} />}
            cursor={<CustomCrosshairCursor />}
          />

          <Legend
            verticalAlign="bottom"
            align="left"
            wrapperStyle={{ fontSize: 14, fontWeight: 600, paddingTop: 10, bottom: 0, color: COR_TEXTO }}
          />

          {data.isotherms.map((iso) => (
            <Line
              key={iso.name}
              data={iso.points}
              dataKey="p"
              stroke={COR_ISO}
              strokeDasharray="6 6"
              strokeWidth={1.2}
              dot={false}
              activeDot={false}
              opacity={0.95}
              legendType="none"
              type="monotone"
              isAnimationActive={false}
            />
          ))}

          <Scatter
            data={topTemperatureLabels}
            dataKey="p"
            fill="transparent"
            legendType="none"
            isAnimationActive={false}
            shape={() => null}
          >
            <LabelList
              dataKey="label"
              position="top"
              offset={10}
              style={{ fill: COR_TEXTO, fontSize: 14, fontWeight: 600 }}
            />
          </Scatter>

          <Scatter
            data={isothermLabels}
            dataKey="p"
            fill="transparent"
            legendType="none"
            isAnimationActive={false}
            shape={() => null}
          >
            <LabelList dataKey="label" content={renderIsothermLabel} />
          </Scatter>

          <Line
            data={data.saturationLiquid}
            dataKey="p"
            stroke={COR_SAT}
            strokeWidth={3}
            dot={false}
            type="monotone"
            name={getTextFunc('saturation_curve')}
            isAnimationActive={false}
          />

          <Line
            data={data.saturationVapor}
            dataKey="p"
            stroke={COR_SAT}
            strokeWidth={3}
            dot={false}
            type="monotone"
            legendType="none"
            isAnimationActive={false}
          />

          <Line
            data={data.cycle}
            dataKey="p"
            stroke={COR_CICLO}
            strokeWidth={4}
            dot={{ r: 6, fill: COR_CICLO }}
            activeDot={{ r: 7 }}
            type="linear"
            name={getTextFunc('refrigeration_cycle')}
            isAnimationActive={false}
            label={({ x, y, index }) => {
              const point = data.cycle[index];
              if (!point || !point.point) {
                return null;
              }

              return (
                <text
                  x={Number(x)}
                  y={Number(y) - 10}
                  fontSize={14}
                  fontWeight={600}
                  textAnchor="middle"
                  fill="#6b6b6b"
                >
                  {point.point}
                </text>
              );
            }}
          />

          {selection.left !== null &&
          selection.right !== null &&
          selection.top !== null &&
          selection.bottom !== null ? (
            <ReferenceArea
              x1={selection.left}
              x2={selection.right}
              y1={selection.top}
              y2={selection.bottom}
              strokeOpacity={0.35}
              fill="#90caf9"
              fillOpacity={0.16}
            />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default PHDiagramChart;
